import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from "@tanstack/react-router";
import { PROJECT } from "@/app/capabilities/permissions";
import { mcpGatewayQueryOptions } from "@/app/mcp-gateways/api/get-mcp-gateway";
import { GatewayEditPage } from "@/app/mcp-gateways/create-page/gateway-create-page";
import {
  mcpGatewayEditSearchDefaults,
  mcpGatewayEditSearchSchema,
} from "@/app/mcp-gateways/search-params";
import { isHttpError } from "@/lib/http-error";
import { requireProjectPermission } from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/mcp-gateways/$gatewayId/edit"
)({
  validateSearch: mcpGatewayEditSearchSchema,
  search: {
    middlewares: [stripSearchParams(mcpGatewayEditSearchDefaults)],
  },
  async beforeLoad(ctx) {
    // Readers open the same page to inspect configuration. The form locks
    // itself when they lack update, so the route only requires the read.
    await requireProjectPermission(ctx, PROJECT.mcpGateways.read);

    const scope = {
      organizationId: ctx.params.organizationId,
      projectId: ctx.params.projectId,
    };
    const options = mcpGatewayQueryOptions({
      binding: ctx.search.binding,
      gatewayId: ctx.params.gatewayId,
      scope,
    });
    try {
      await ctx.context.queryClient.ensureQueryData({
        ...options,
        revalidateIfStale: true,
      });
    } catch (error) {
      if (isHttpError(error) && error.status === 404) {
        throw redirect({
          params: {
            organizationId: ctx.params.organizationId,
            projectId: ctx.params.projectId,
          },
          to: "/orgs/$organizationId/projects/$projectId/mcp-gateways",
        });
      }
      throw error;
    }
  },
  loader: () => ({ crumb: "Edit gateway" }),
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, projectId, gatewayId } = Route.useParams();
  const { binding } = Route.useSearch();
  const scope = { organizationId, projectId };
  const { data: gateway } = useSuspenseQuery(
    mcpGatewayQueryOptions({
      binding,
      gatewayId,
      scope,
    })
  );

  return <GatewayEditPage gateway={gateway} key={gateway.id} />;
}
